import React, { useEffect, useId, useMemo, useRef, useState } from "react";

/**
 * Compact professional select with type-to-search.
 * options: [{ value, label }] or primitive values
 */
const CompactSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  searchPlaceholder = "Search…",
  searchable,
  className = "",
  menuClassName = "",
  ariaLabel,
  disabled = false,
  size = "md", // sm | md
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();

  const normalized = useMemo(
    () =>
      options.map((opt) =>
        typeof opt === "object" && opt !== null
          ? { value: opt.value, label: opt.label ?? String(opt.value) }
          : { value: opt, label: String(opt) }
      ),
    [options]
  );

  const canSearch =
    searchable !== undefined ? Boolean(searchable) : size !== "sm";

  const selected =
    normalized.find((o) => String(o.value) === String(value)) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter((o) =>
      String(o.label || "").toLowerCase().includes(q)
    );
  }, [normalized, query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    const t = setTimeout(() => searchRef.current?.focus(), 20);
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (next) => {
    onChange?.(next);
    setOpen(false);
    setQuery("");
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[0]) pick(filtered[0].value);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`ds-select ${size === "sm" ? "ds-select--sm" : ""} ${
        open ? "is-open" : ""
      } ${disabled ? "is-disabled" : ""} ${
        canSearch ? "ds-select--search" : ""
      } ${className}`.trim()}
    >
      <button
        type="button"
        className="ds-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel || placeholder}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        <span className="ds-select__value">
          {selected ? selected.label : placeholder}
        </span>
        <span className="ds-select__chevron" aria-hidden>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M2 3.5L5 6.5L8 3.5"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      <div
        className={`ds-select__menu ${menuClassName}`.trim()}
        role="presentation"
      >
        {canSearch ? (
          <div className="ds-select__search-wrap">
            <input
              ref={searchRef}
              type="search"
              className="ds-select__search"
              value={query}
              placeholder={searchPlaceholder}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              onClick={(e) => e.stopPropagation()}
              autoComplete="off"
            />
          </div>
        ) : null}
        <ul id={listId} className="ds-select__list" role="listbox">
          {filtered.length === 0 ? (
            <li className="ds-select__empty">No match</li>
          ) : (
            filtered.map((opt) => {
              const active = String(opt.value) === String(value);
              return (
                <li
                  key={String(opt.value)}
                  role="option"
                  aria-selected={active}
                >
                  <button
                    type="button"
                    className={`ds-select__option ${active ? "is-active" : ""}`}
                    onClick={() => pick(opt.value)}
                  >
                    {opt.label}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
};

export default CompactSelect;
